import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { registerUser } from "../../redux/slices/auth";
import { Link, useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import InputPassword from "../../components/InputPassword/InputPassword";
import "./RegisterForm.css";
import { toast } from "react-toastify";
import { ROLE_OPTIONS } from "../../constants/roles";

const RegisterForm = () => {
  const dispatch = useDispatch();
  const { status, isAuthenticated } = useSelector((state) => state.auth);
  const navigate = useNavigate();

  useEffect(() => {
    if (isAuthenticated) {
      navigate("/"); // redirect after registration
    }
  }, [isAuthenticated, navigate]);

  const validationSchema = Yup.object().shape({
    username: Yup.string()
      .min(3, "Username must be at least 3 characters")
      .matches(
        /^[a-zA-Z0-9_]+$/,
        "Username must contain only letters, numbers, and underscores"
      )
      .required("Username is required"),
    email: Yup.string()
      .email("Please enter a valid email address")
      .required("Email is required"),
    password: Yup.string()
      .min(8, "Password must be at least 8 characters")
      .matches(/(?=.*[a-z])/, "Must contain at least one lowercase letter")
      .matches(/(?=.*[A-Z])/, "Must contain at least one uppercase letter")
      .matches(/(?=.*\d)/, "Must contain at least one number")
      .matches(
        /(?=.*[@$!%*?&])/,
        "Must contain at least one special character (!@#$%^&*)"
      )
      .required("Password is required"),
    confirm_password: Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords do not match")
      .required("Please confirm your password"),
    first_name: Yup.string()
      .matches(
        /^[a-zA-Z\s]*$/,
        "First name must contain only letters and spaces"
      )
      .nullable(),
    last_name: Yup.string()
      .matches(
        /^[a-zA-Z\s]*$/,
        "Last name must contain only letters and spaces"
      )
      .nullable(),
    role: Yup.string()
      .oneOf(["customer", "author"])
      .required("Please select a role"),
  });

  const formik = useFormik({
    initialValues: {
      username: "",
      email: "",
      password: "",
      confirm_password: "",
      first_name: "",
      last_name: "",
      role: "customer",
    },
    validationSchema,
    onSubmit: async (values) => {
      try {
        // Remove confirm_password from values before sending to API
        const { confirm_password, ...registerData } = values;
        const result = await dispatch(registerUser(registerData));

        if (result.payload.success) {
          toast.success(result.payload.message);
          navigate("/login");
        } else {
          toast.error(result.payload.message);
        }
      } catch (error) {
        toast.error("Registration failed. Please try again.");
      }
    },
  });

  return (
    <section className="RegisterForm">
      <div className="register-form-wrapper">
        <form onSubmit={formik.handleSubmit}>
          <h3>Create your Account</h3>
          <p>Join us to start streaming amazing content</p>

          {/* First Name Field */}
          <div className="register-form-group">
            <input
              type="text"
              name="first_name"
              placeholder="First Name (Optional)"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.first_name}
              className={
                formik.touched.first_name && formik.errors.first_name
                  ? "error"
                  : ""
              }
            />
            {formik.touched.first_name && formik.errors.first_name && (
              <div className="error-message">
                <p>{formik.errors.first_name}</p>
              </div>
            )}
          </div>

          {/* Last Name Field */}
          <div className="register-form-group">
            <input
              type="text"
              name="last_name"
              placeholder="Last Name (Optional)"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.last_name}
              className={
                formik.touched.last_name && formik.errors.last_name
                  ? "error"
                  : ""
              }
            />
            {formik.touched.last_name && formik.errors.last_name && (
              <div className="error-message">
                <p>{formik.errors.last_name}</p>
              </div>
            )}
          </div>

          {/* Username Field */}
          <div className="register-form-group">
            <input
              type="text"
              name="username"
              placeholder="Username"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.username}
              className={
                formik.touched.username && formik.errors.username ? "error" : ""
              }
            />
            {formik.touched.username && formik.errors.username && (
              <div className="error-message">
                <p>{formik.errors.username}</p>
              </div>
            )}
          </div>

          {/* Email Field */}
          <div className="register-form-group">
            <input
              type="email"
              name="email"
              placeholder="Email"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.email}
              className={
                formik.touched.email && formik.errors.email ? "error" : ""
              }
            />
            {formik.touched.email && formik.errors.email && (
              <div className="error-message">
                <p>{formik.errors.email}</p>
              </div>
            )}
          </div>

          {/* Password Field */}
          <div className="register-form-group">
            <InputPassword
              name="password"
              placeholder="Password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.password}
              className={
                formik.touched.password && formik.errors.password ? "error" : ""
              }
            />
            {formik.touched.password && formik.errors.password && (
              <div className="error-message">
                <p>{formik.errors.password}</p>
              </div>
            )}
          </div>

          {/* Confirm Password Field */}
          <div className="register-form-group">
            <InputPassword
              name="confirm_password"
              placeholder="Confirm Password"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.confirm_password}
              className={
                formik.touched.confirm_password &&
                formik.errors.confirm_password
                  ? "error"
                  : ""
              }
            />
            {formik.touched.confirm_password &&
              formik.errors.confirm_password && (
                <div className="error-message">
                  <p>{formik.errors.confirm_password}</p>
                </div>
              )}
          </div>

          {/* Role Selection */}
          <div className="register-form-group">
            <select
              name="role"
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              value={formik.values.role}
              className={
                formik.touched.role && formik.errors.role ? "error" : ""
              }
            >
              {ROLE_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
            {formik.touched.role && formik.errors.role && (
              <div className="error-message">
                <p>{formik.errors.role}</p>
              </div>
            )}
          </div>

          {/* Submit Button */}
          <button
            type="submit"
            disabled={status === "loading" || formik.isSubmitting}
          >
            {status === "loading" ? "Creating Account..." : "Create Account"}
          </button>

          {/* Login Link */}
          <p className="login-link">
            Already have an account? <Link to="/login">Log in</Link>
          </p>
        </form>
      </div>
    </section>
  );
};

export default RegisterForm;
